import {
  toggleGroupTag,
  type GroupEnrollment,
  type GroupMemberRole,
  type GroupVisibility,
  type RecurringSeries,
} from '@church/domain'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useApi } from '../api-context.tsx'
import { useMyProfile } from '../session/useMyProfile.ts'
import { groupKeys, type GroupListFilters } from './keys.ts'

/**
 * Group hooks.
 *
 * Every group the API returns carries an `access` object — what this actor may
 * do with *this* group. Screens branch on that rather than on the role, because
 * a group leader is staff for one group and a stranger to the next.
 *
 * Membership changes ripple: a join alters the roster, the directory (member
 * counts, "my groups") and the person's profile, so mutations invalidate all
 * three rather than trying to patch caches by hand.
 */

export function useGroupTypes() {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.types(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups.types.get()
      if (error) throw error
      return data
    },
  })
}

export type GroupType = NonNullable<ReturnType<typeof useGroupTypes>['data']>[number]

export function useGroups(filters: GroupListFilters = {}) {
  const api = useApi()
  const query = useInfiniteQuery({
    queryKey: groupKeys.list(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error } = await api.api.v1.groups.get({
        query: { ...filters, ...(pageParam ? { cursor: pageParam } : {}) },
      })
      if (error) throw error
      return data
    },
    getNextPageParam: (last) => last.nextCursor,
  })

  return {
    groups: query.data?.pages.flatMap((page) => page.items) ?? [],
    fetchNextPage: query.fetchNextPage,
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isPending,
    error: query.error,
    refetch: query.refetch,
    isRefetching: query.isRefetching,
  }
}

export type Group = ReturnType<typeof useGroups>['groups'][number]
export type GroupAccess = Group['access']

export function useGroup(groupId: string) {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.detail(groupId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).get()
      if (error) throw error
      return data
    },
  })
}

/**
 * The roster. Enabled only when the caller says the actor may see it, so a
 * closed roster shows an explanation instead of firing a request that 403s.
 */
export function useGroupRoster(groupId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.roster(groupId),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).members.get()
      if (error) throw error
      return data
    },
  })
}

export type RosterMember = NonNullable<ReturnType<typeof useGroupRoster>['data']>[number]

export function useGroupRequests(groupId: string, enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.requests(groupId),
    enabled,
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).requests.get()
      if (error) throw error
      return data
    },
  })
}

export function usePersonGroups(personId: string) {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.forPerson(personId),
    queryFn: async () => {
      const { data, error } = await api.api.v1.people({ id: personId }).groups.get()
      if (error) throw error
      return data
    },
  })
}

/** Anything that changes who is in a group touches these four caches. */
function useMembershipInvalidation() {
  const queryClient = useQueryClient()
  return (groupId: string, personId?: string) => {
    void queryClient.invalidateQueries({ queryKey: groupKeys.roster(groupId) })
    void queryClient.invalidateQueries({ queryKey: groupKeys.requests(groupId) })
    void queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
    void queryClient.invalidateQueries({ queryKey: groupKeys.all(), predicate: isListQuery })
    if (personId) void queryClient.invalidateQueries({ queryKey: groupKeys.forPerson(personId) })
  }
}

const isListQuery = (query: { queryKey: readonly unknown[] }) => query.queryKey[1] === 'list'

function useGroupInvalidation() {
  const queryClient = useQueryClient()
  return (groupId?: string) => {
    if (groupId) void queryClient.invalidateQueries({ queryKey: groupKeys.detail(groupId) })
    void queryClient.invalidateQueries({ queryKey: groupKeys.all(), predicate: isListQuery })
  }
}

export interface GroupInput {
  groupTypeId: string
  campusId?: string | null
  name: string
  description?: string | null
  location?: string | null
  capacity?: number | null
  schedule?: RecurringSeries | null
  contactEmail?: string | null
  virtualUrl?: string | null
}

/** A group's own privacy (staff only): null puts each back to its type's. */
export interface GroupSettingsInput {
  visibility?: GroupVisibility | null
  enrollment?: GroupEnrollment | null
  rosterVisibleToMembers?: boolean | null
}

export function useCreateGroup() {
  const api = useApi()
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: async (body: GroupInput) => {
      const { data, error } = await api.api.v1.groups.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (data) => invalidate(data.id),
  })
}

export function useUpdateGroup(groupId: string) {
  const api = useApi()
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: async (body: Partial<GroupInput> & GroupSettingsInput) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(groupId),
  })
}

export function useArchiveGroup(groupId: string) {
  const api = useApi()
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: async (archived: boolean) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).archive.post({ archived })
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(groupId),
  })
}

export function useDeleteGroup() {
  const api = useApi()
  const invalidate = useGroupInvalidation()
  return useMutation({
    mutationFn: async (groupId: string) => {
      const { error } = await api.api.v1.groups({ id: groupId }).delete()
      if (error) throw error
    },
    onSuccess: () => invalidate(),
  })
}

export function useAddGroupMember(groupId: string) {
  const api = useApi()
  const invalidate = useMembershipInvalidation()
  return useMutation({
    mutationFn: async (body: { personId: string; role?: GroupMemberRole }) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).members.post(body)
      if (error) throw error
      return data
    },
    onSuccess: (_data, body) => invalidate(groupId, body.personId),
  })
}

export function useSetGroupMemberRole(groupId: string) {
  const api = useApi()
  const invalidate = useMembershipInvalidation()
  return useMutation({
    mutationFn: async (body: { personId: string; role: GroupMemberRole }) => {
      const { data, error } = await api.api.v1
        .groups({ id: groupId })
        .members({ personId: body.personId })
        .patch({ role: body.role })
      if (error) throw error
      return data
    },
    onSuccess: (_data, body) => invalidate(groupId, body.personId),
  })
}

export function useRemoveGroupMember(groupId: string) {
  const api = useApi()
  const invalidate = useMembershipInvalidation()
  return useMutation({
    mutationFn: async (personId: string) => {
      const { error } = await api.api.v1.groups({ id: groupId }).members({ personId }).delete()
      if (error) throw error
    },
    onSuccess: (_data, personId) => invalidate(groupId, personId),
  })
}

/**
 * Leaving, as yourself. The API removes a member by person id, and the client
 * learns its own from the linked directory record — a login with no person
 * cannot be in a group, so `isReady` is false and the button stays out.
 */
export function useLeaveGroup(groupId: string) {
  const { data: profile } = useMyProfile()
  const remove = useRemoveGroupMember(groupId)
  return {
    isReady: !!profile,
    isPending: remove.isPending,
    error: remove.error,
    leave: () => {
      if (profile) remove.mutate(profile.id)
    },
  }
}

export function useDecideGroupRequest(groupId: string) {
  const api = useApi()
  const invalidate = useMembershipInvalidation()
  return useMutation({
    mutationFn: async (body: { personId: string; decision: 'approve' | 'decline' }) => {
      const { error } = await api.api.v1
        .groups({ id: groupId })
        .requests({ personId: body.personId })
        .post({ decision: body.decision })
      if (error) throw error
    },
    onSuccess: (_data, body) => invalidate(groupId, body.personId),
  })
}

/** Join, or ask to join, as yourself. */
export function useJoinGroup(groupId: string) {
  const api = useApi()
  const invalidate = useMembershipInvalidation()
  return useMutation({
    mutationFn: async (body: { note?: string | null } = {}) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).join.post(body)
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(groupId),
  })
}

export function useGroupTypeMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: groupKeys.types() })
    void queryClient.invalidateQueries({ queryKey: groupKeys.all(), predicate: isListQuery })
  }

  interface TypeInput {
    name: string
    description?: string | null
    visibility?: 'public' | 'members' | 'private'
    enrollment?: 'open' | 'request' | 'invite'
    rosterVisibleToMembers?: boolean
  }

  const create = useMutation({
    mutationFn: async (body: TypeInput) => {
      const { data, error } = await api.api.v1.groups.types.post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const update = useMutation({
    mutationFn: async ({ id, ...body }: Partial<TypeInput> & { id: string }) => {
      const { data, error } = await api.api.v1.groups.types({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.groups.types({ id }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { create, update, remove }
}

// ---- picture ---------------------------------------------------------------------

/** Where a group's picture is served from: proxied, so the session applies. */
export const groupImageUrl = (groupId: string, version?: string | null) =>
  `/api/v1/groups/${groupId}/image${version ? `?v=${encodeURIComponent(version)}` : ''}`

export const GROUP_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'] as const
type GroupImageType = (typeof GROUP_IMAGE_TYPES)[number]

/** The picture in three steps — a slot, the bytes straight to storage, confirm — and removing it. */
export function useGroupImage(groupId: string) {
  const api = useApi()
  const invalidate = useGroupInvalidation()

  const upload = useMutation({
    mutationFn: async (file: { type: string; body: Blob }) => {
      if (!GROUP_IMAGE_TYPES.includes(file.type as GroupImageType))
        throw new Error('A group picture must be JPEG, PNG or WebP.')
      const slot = await api.api.v1.groups({ id: groupId }).image['upload-url'].post({
        contentType: file.type as GroupImageType,
        sizeBytes: file.body.size,
      })
      if (slot.error) throw slot.error
      const uploaded = await fetch(slot.data.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file.body,
      })
      if (!uploaded.ok) throw new Error(`Storage rejected the upload (${uploaded.status}).`)
      const { data, error } = await api.api.v1.groups({ id: groupId }).image.put({
        key: slot.data.key,
      })
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(groupId),
  })

  const remove = useMutation({
    mutationFn: async () => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).image.delete()
      if (error) throw error
      return data
    },
    onSuccess: () => invalidate(groupId),
  })

  return { upload, remove }
}

// ---- tags ------------------------------------------------------------------------

export function useGroupTagGroups() {
  const api = useApi()
  return useQuery({
    queryKey: groupKeys.tagGroups(),
    queryFn: async () => {
      const { data, error } = await api.api.v1.groups['tag-groups'].get()
      if (error) throw error
      return data
    },
  })
}

export type GroupTagGroup = NonNullable<ReturnType<typeof useGroupTagGroups>['data']>[number]

/** The vocabulary, for admins: categories and their tags. */
export function useGroupTagMutations() {
  const api = useApi()
  const queryClient = useQueryClient()
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: groupKeys.tagGroups() })
    // Renaming or deleting a tag changes every group that carries it.
    void queryClient.invalidateQueries({ queryKey: groupKeys.all() })
  }

  interface FacetInput {
    name?: string
    multiple?: boolean
    shownInDirectory?: boolean
    position?: number
  }

  const createFacet = useMutation({
    mutationFn: async (body: { name: string; multiple?: boolean; shownInDirectory?: boolean }) => {
      const { data, error } = await api.api.v1.groups['tag-groups'].post(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const updateFacet = useMutation({
    mutationFn: async ({ id, ...body }: FacetInput & { id: string }) => {
      const { data, error } = await api.api.v1.groups['tag-groups']({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const removeFacet = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.groups['tag-groups']({ id }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })
  const createTag = useMutation({
    mutationFn: async ({ tagGroupId, name }: { tagGroupId: string; name: string }) => {
      const { data, error } = await api.api.v1.groups['tag-groups']({ id: tagGroupId }).tags.post({
        name,
      })
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const updateTag = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; position?: number }) => {
      const { data, error } = await api.api.v1.groups.tags({ id }).patch(body)
      if (error) throw error
      return data
    },
    onSuccess: invalidate,
  })
  const removeTag = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await api.api.v1.groups.tags({ id }).delete()
      if (error) throw error
    },
    onSuccess: invalidate,
  })

  return { createFacet, updateFacet, removeFacet, createTag, updateTag, removeTag }
}

/**
 * A group's tags, edited as a set: `toggle` follows the category's rule (a
 * one-choice category swaps), `save` sends the lot.
 */
export function useGroupTagsEditor(groupId: string, current: readonly string[]) {
  const api = useApi()
  const invalidate = useGroupInvalidation()
  const { data: facets } = useGroupTagGroups()
  const [draft, setDraft] = useState<string[] | null>(null)
  const selected = draft ?? [...current]

  const allTags = (facets ?? []).flatMap((facet) =>
    facet.tags.map((tag) => ({ id: tag.id, tagGroupId: facet.id })),
  )

  const save = useMutation({
    mutationFn: async (tagIds: string[]) => {
      const { data, error } = await api.api.v1.groups({ id: groupId }).tags.put({ tagIds })
      if (error) throw error
      return data
    },
    onSuccess: () => {
      setDraft(null)
      invalidate(groupId)
    },
  })

  return {
    facets: facets ?? [],
    selected,
    dirty: draft !== null,
    toggle: (tagId: string) => {
      const tag = allTags.find((t) => t.id === tagId)
      if (!tag) return
      setDraft(toggleGroupTag(selected, tag, allTags, facets ?? []))
    },
    reset: () => setDraft(null),
    save,
  }
}

/**
 * The directory's tag filter: one choice per category, sent as the
 * comma-separated `tags` the API takes.
 */
export function useGroupTagFilter() {
  const { data: facets } = useGroupTagGroups()
  const [byFacet, setByFacet] = useState<Record<string, string>>({})
  const chosen = Object.values(byFacet).filter(Boolean)
  return {
    facets: (facets ?? []).filter((facet) => facet.tags.length > 0),
    value: (facetId: string) => byFacet[facetId] ?? '',
    set: (facetId: string, tagId: string) => setByFacet((prev) => ({ ...prev, [facetId]: tagId })),
    param: chosen.length > 0 ? chosen.join(',') : undefined,
  }
}
